import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "./ClientPlatformAnalytics.css";
import "./PublicPlatformReport.css";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
// The Cphere brand footer is ONE component, shared with the Campaign Strategy
// Microsites. A public report renders the approved footer — brain mark and
// all — not a second version of it.
import CphereBrandFooter, { PUBLIC_REPORT_FOOTER_LEAD } from "../components/CphereBrandFooter";
import { resolvePlatformBranding } from "../utils/platformBranding";
import {
  PLATFORM_ANALYTICS_DATE_PRESETS,
  PLATFORM_ANALYTICS_METRICS,
  buildCampaignKpiStrip,
  buildDailyDeliveryChartModel,
  buildExplicitWindow,
  buildPlatformAnalyticsModel,
  buildPlatformCardPresentation,
  buildPresetWindow,
  focusPlatformAnalyticsModel,
  formatAnalyticsValue,
  formatRatePercent,
  isIsoDayString,
  summaryDailyByChannel,
} from "../platformAnalytics/platformAnalyticsReportModel";
// Which efficiency rate actually describes a platform: CTV is read on video
// view rate, click-bought inventory on CTR. One shared resolver, so a CTV
// column can never state the meaningless 0.00% CTR again.
import {
  resolvePlatformEfficiencyMetric,
  readPlatformCounts,
} from "../platformAnalytics/platformEfficiencyMetric";
// The delivery visualization is ONE implementation, shared with Quantum's
// /cross-platform-analytics and the authenticated Cortex page. This report
// owns the canonical window totals; it does not own a chart of its own.
import DeliveryChart, { DeliveryChartLegend } from "../platformAnalytics/DeliveryChart";
import {
  resolveObservedSiteVisits,
  OBSERVED_GA4_SITE_VISITS_LABEL,
} from "../platformAnalytics/observedSiteVisits";
import { buildWindowTotalsDeliveryChartModel } from "../platformAnalytics/deliveryChartModel";
// The platform contribution card is ONE component, shared with Quantum's
// /cross-platform-analytics rail and the authenticated Cortex page. A public
// report renders the canonical card, not a reduced version of it.
import PlatformContributionCard from "../platformAnalytics/PlatformContributionCard";
// Governed metric definitions live once, in the shared (byte-pinned) profile
// layer, so the public tile and the per-platform pages can never explain the
// same number differently. Aliased: this page's KPI-strip registry also
// exports PLATFORM_ANALYTICS_METRICS from the report model.
import { PLATFORM_ANALYTICS_METRICS as PPX_METRIC_PROFILES } from "../platformAnalytics/platformAnalyticsProfiles";
// The governed name for the outcome tile's conversions figure. The
// classification travels on the strip card; the definition sentence is the
// byte-pinned shared one, extended below with the aggregate-only clause.
import {
  AD_ATTRIBUTED_SITE_VISITS_CLASSIFICATION,
  AD_ATTRIBUTED_SITE_VISITS_DEFINITION,
} from "../platformAnalytics/outcomeSemantics";
import {
  buildCampaignScopeFlightWindow,
  buildCampaignScopeFullDeliveryWindow,
  campaignDeliveryExtensionProvenance,
  campaignFullDeliveryProvenance,
  campaignNoDeliveryState,
  campaignSelectorLabel,
} from "../platformAnalytics/campaignReportingLifecycle";
import {
  reportingTimeZoneLabel,
  reportingTimeZoneStatement,
  responseReportingTimeZone,
  sourceCalendarQualification,
} from "../utils/reportingCalendar";

const CONVERSIONS_BASE_DEFINITION =
  PPX_METRIC_PROFILES.find((metric) => metric.key === "conversions")?.definition || null;

/*
 * The aggregate tile adds the clauses that only exist at aggregation time:
 * per-platform numbers are summed with no cross-platform deduplication, and
 * GA4 measurement can never enter the paid total (it reports separately as
 * Site Visits). The per-platform base sentence stays single-sourced in the
 * byte-pinned profile layer.
 */
const CONVERSIONS_METRIC_DEFINITION = CONVERSIONS_BASE_DEFINITION
  ? `${CONVERSIONS_BASE_DEFINITION} Totals sum across delivering platforms without cross-platform deduplication. GA4 site activity is never included — it reports separately as Site Visits.`
  : null;

/*
 * The verified case gets the same aggregate clause. The GA4 distinction is
 * already part of the shared definition sentence, so it is not repeated.
 */
const AD_ATTRIBUTED_SITE_VISITS_METRIC_DEFINITION = `${AD_ATTRIBUTED_SITE_VISITS_DEFINITION} Totals sum across delivering platforms without cross-platform deduplication.`;

/*
 * Screen-reader-only text for governed metric definitions. Inline so the
 * byte-pinned cpx stylesheets carry no new classes; pointer users get the
 * same text via `title`.
 */
const METRIC_DEFINITION_SR_ONLY_STYLE = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

/**
 * Public Platform Analytics report — /report/:publicReportToken
 *
 * A shareable, unauthenticated, read-only view of one advertiser's platform
 * performance. Three properties define it:
 *
 *  1. SCOPE COMES FROM THE TOKEN. This page never sends an advertiser id, and
 *     there is no code path here that could. The URL segment is the entire
 *     credential; the server resolves it to a governed report configuration
 *     and answers only within that scope. Editing the URL cannot reach another
 *     advertiser — it can only stop resolving.
 *
 *  2. THE NUMBERS ARE THE AUTHENTICATED NUMBERS. Every value on this page is
 *     produced by buildPlatformAnalyticsModel from the canonical reporting
 *     payload, exactly as /platform-analytics produces its own. Nothing here
 *     recomputes, re-aggregates, or re-rounds anything.
 *
 *  3. NO APPLICATION CHROME. No sidebar, no account navigation, no advertiser
 *     selector, no integrations, no governance actions, no diagnostics, no
 *     links back into the app, and no write action of any kind. A viewer of
 *     this page cannot navigate into Cphere from it.
 *
 * When canonical evidence is absent the page says so plainly. It never
 * estimates, interpolates, or fills a chart to look complete.
 */

const API_BASE_URL = (
  process.env.REACT_APP_BACKEND_URL || "https://cphere-backend.onrender.com"
).replace(/\/+$/, "");

// The report token as it appears in the path. Anything that is not an opaque
// base64url handle is refused here rather than sent to the server.
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,128}$/;

export const isWellFormedReportToken = (value) => TOKEN_PATTERN.test(String(value || ""));

/**
 * The campaign flight makes a better default window for a campaign report
 * than a rolling 30 days, so it is offered as a preset. It is still just a
 * date range: it selects which canonical window is requested and can never
 * change what exists inside it.
 */
export const buildCampaignFlightPreset = (campaignOrCampaigns, campaignKey = "") => {
  const campaigns = Array.isArray(campaignOrCampaigns)
    ? campaignOrCampaigns
    : campaignOrCampaigns
    ? [campaignOrCampaigns]
    : [];
  const window = buildCampaignScopeFlightWindow(campaigns, campaignKey);
  if (!window) return null;
  const { startDate: flightStartDate, endDate: flightEndDate } = window;
  if (!isIsoDayString(flightStartDate) || !isIsoDayString(flightEndDate)) return null;
  if (flightStartDate > flightEndDate) return null;
  return { key: "campaign_flight", label: "Campaign Flight", flightStartDate, flightEndDate };
};

/**
 * FULL DELIVERY — the campaign's actual delivery lifecycle: start through
 * max(latest reported delivery, authorized delivery end, original flight
 * end). A reporting view, never an authorization claim, and offered only
 * when it ends strictly later than the Campaign Flight preset for the same
 * scope. Carries the same date property names as the flight preset so both
 * resolve through resolveReportWindow's one explicit-window path.
 */
export const buildCampaignFullDeliveryPreset = (campaignOrCampaigns, campaignKey = "") => {
  const campaigns = Array.isArray(campaignOrCampaigns)
    ? campaignOrCampaigns
    : campaignOrCampaigns
    ? [campaignOrCampaigns]
    : [];
  const window = buildCampaignScopeFullDeliveryWindow(campaigns, campaignKey);
  if (!window) return null;
  const { startDate: flightStartDate, endDate: flightEndDate } = window;
  if (!isIsoDayString(flightStartDate) || !isIsoDayString(flightEndDate)) return null;
  if (flightStartDate > flightEndDate) return null;
  return { key: "full_delivery", label: "Full Delivery", flightStartDate, flightEndDate };
};

export const resolveReportWindow = (presetKey, flightPreset, reference = new Date()) => {
  if (flightPreset && presetKey === flightPreset.key) {
    return buildExplicitWindow(flightPreset.flightStartDate, flightPreset.flightEndDate);
  }
  return buildPresetWindow(presetKey, reference);
};

const REPORT_PHASES = Object.freeze({
  LOADING: "loading",
  UNAVAILABLE: "unavailable",
  // A protected report the server has not authorized this browser for. No
  // report data has been fetched in this state and none can be: the server
  // refuses the payload, so the gate is a boundary rather than a curtain.
  ACCESS_REQUIRED: "access_required",
  READY: "ready",
  EMPTY: "empty",
  ERROR: "error",
});

/**
 * Every request this page makes must carry the remembered-access cookie, and
 * the cookie is HttpOnly — the page cannot read it, cannot forge it, and
 * cannot decide what it means. All it does is let the browser send it.
 */
const CREDENTIALED = { credentials: "include", cache: "no-store" };

/*
 * The Cphere access gate.
 *
 * Deliberately the SAME shell as the report it guards: the canonical brand
 * mark, the Platform / Analytics hero pill, the shimmer bar and one cpx-panel.
 * A recipient who is asked for a code should be looking at something they can
 * recognize as Cphere before they type a credential into it — an unbranded
 * password box on an unfamiliar domain is exactly what people are told not to
 * trust.
 *
 * It identifies NOTHING about the report. No advertiser, no campaign, no
 * label — the server does not send those before authorization, so a forwarded
 * link does not even confirm whose report it opens.
 */
function ProtectedReportGate({ onSubmit, status, submitting }) {
  const [code, setCode] = useState("");

  return (
    <div className="prx-page" data-public-platform-report="protected">
      <div className="cpx-page-container prx-page-container">
        <div className="cpx-page-content prx-page-content">
          <CphereHeaderLogo className="cpx-brand-logo" />

          <header className="cphere-governed-hero-selector-stack prx-hero-stack">
            <div className="cpx-hero-container">
              <div
                className="cpx-hero-pill"
                role="heading"
                aria-level="1"
                aria-label="Platform Analytics"
              >
                <span className="cpx-hero-pill-label cphere-hero-pill-wordmark">Platform</span>
                <span className="cpx-hero-pill-tag cphere-hero-pill-tag">Analytics</span>
              </div>
              <div className="cpx-hero-shimmer-bar" aria-hidden="true" />
            </div>
          </header>

          <section className="cpx-panel prx-access-panel" aria-label="Protected report">
            <p className="prx-access-eyebrow">Protected report</p>
            <h2 className="prx-access-title">Enter your security code</h2>

            <form
              className="prx-access-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (!submitting) onSubmit(code);
              }}
            >
              <label className="prx-access-label" htmlFor="prx-access-code">
                Security code
              </label>
              <input
                id="prx-access-code"
                className="prx-access-input cpx-numeric"
                name="code"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                spellCheck="false"
                placeholder="XXXX-XXXX-XXXX"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                disabled={submitting}
                aria-describedby="prx-access-help"
              />

              {status ? (
                <p className="prx-access-status" role="alert">
                  {status}
                </p>
              ) : null}

              <button
                type="submit"
                className="prx-access-submit"
                disabled={submitting || !code.trim()}
              >
                {submitting ? "Checking…" : "Access report"}
              </button>
            </form>

            <p className="prx-access-help" id="prx-access-help">
              Your security code was provided separately by Cphere. This browser will
              stay authorized for 30 days.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

/*
 * Site Visits · GA4 on a public report.
 *
 * Same contract as the authenticated chart: a day GA4 never reported leaves a
 * HOLE, because the subpath restarts rather than continuing, and bridging a gap
 * would draw a straight segment that reads as data. A reported 0 is plotted —
 * that is an observation. The series is omitted entirely when GA4 reported
 * nothing, so this renders only when there is something real to show.
 *
 * Correlation, not attribution for the ADVERTISER-WIDE series: it states
 * observed website traffic beside media delivery and does not claim the media
 * caused it. A campaign whose outcome semantics the backend verified carries a
 * different series under a different governed name, and the shared resolver —
 * not a ternary here — is what tells the two apart.
 */
function PublicSiteVisitsChart({ siteVisits }) {
  const days = Array.isArray(siteVisits?.days) ? siteVisits.days : [];
  if (!days.length) return null;
  /*
   * The name comes off the shared resolver, which is also what keeps a stale
   * payload label from outranking the governed vocabulary. Spelling the
   * classification and its label out again here is how a public report ends up
   * naming the same series something the dashboards do not.
   */
  const siteVisitsLabel =
    resolveObservedSiteVisits(siteVisits)?.label || OBSERVED_GA4_SITE_VISITS_LABEL;

  const width = 640;
  const height = 150;
  const marginLeft = 46;
  const marginRight = 14;
  const marginTop = 12;
  const marginBottom = 22;
  const plotWidth = Math.max(40, width - marginLeft - marginRight);
  const plotHeight = Math.max(30, height - marginTop - marginBottom);

  const maxValue = Math.max(...days.map((day) => day.value));
  const yMax = maxValue > 0 ? maxValue * 1.15 : 1;
  const slot = days.length > 1 ? plotWidth / (days.length - 1) : plotWidth;
  const points = days.map((day, index) => ({
    date: day.date,
    value: day.value,
    x: marginLeft + (days.length > 1 ? index * slot : plotWidth / 2),
    y: marginTop + plotHeight - (day.value / yMax) * plotHeight,
  }));

  let d = "";
  let pendingMove = true;
  points.forEach((point, index) => {
    // Consecutive calendar days stay connected; any break in the sequence
    // restarts the path, which is what makes an unreported day visible.
    if (index > 0) {
      const previous = new Date(`${points[index - 1].date}T00:00:00Z`).getTime();
      const current = new Date(`${point.date}T00:00:00Z`).getTime();
      if (current - previous > 86400000) pendingMove = true;
    }
    d += `${pendingMove ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(1)} `;
    pendingMove = false;
  });

  const windowLabel =
    siteVisits.startDate && siteVisits.endDate
      ? `${siteVisits.startDate} → ${siteVisits.endDate}`
      : "";

  return (
    <section className="cpx-panel cpx-site-visits-panel" aria-label="Site visits over time">
      <div className="cpx-panel-header">
        <div>
          <h2 className="cpx-panel-title">{siteVisitsLabel}</h2>
          <p className="cpx-panel-subtitle">
            Compare daily media delivery with observed website traffic
            {windowLabel ? ` · ${windowLabel}` : ""}
          </p>
        </div>
      </div>

      {/* The canonical Site Visits treatment — the same gridline, axis and
          amber series primitives Quantum's Delivery-and-efficiency chart uses
          for this series, from the same shared cpx- stylesheet. */}
      <div className="cpx-chart-wrap">
        <svg
          className="cpx-chart-svg cpx-site-visits-svg"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={`Observed site visits by day. ${days.length} observed ${
            days.length === 1 ? "day" : "days"
          }.`}
        >
          {[0, 0.5, 1].map((fraction) => {
            const y = marginTop + plotHeight - fraction * plotHeight;
            return (
              <g key={`sv-grid-${fraction}`}>
                <line
                  x1={marginLeft}
                  x2={width - marginRight}
                  y1={y}
                  y2={y}
                  className={
                    fraction === 0
                      ? "cpx-chart-gridline cpx-chart-gridline--baseline"
                      : "cpx-chart-gridline"
                  }
                />
                <text
                  x={marginLeft - 8}
                  y={y + 4}
                  className="cpx-chart-axis-label cpx-chart-axis-label--right cpx-numeric"
                  textAnchor="end"
                >
                  {Math.round(fraction * yMax).toLocaleString()}
                </text>
              </g>
            );
          })}
          <path className="cpx-chart-site-visits-line" d={d.trim()} />
          {points.map((point) => (
            <circle
              key={`sv-dot-${point.date}`}
              className="cpx-chart-site-visits-dot"
              cx={point.x}
              cy={point.y}
              r={3}
            >
              <title>{`${point.date} · Site Visits: ${point.value.toLocaleString()}`}</title>
            </circle>
          ))}
        </svg>
        <div className="cpx-legend" aria-hidden="true">
          <span className="cpx-legend-item">
            <span className="cpx-legend-line cpx-legend-line--visits" />
            {siteVisitsLabel}
          </span>
        </div>
      </div>

      <div className="cpx-site-visits-readout">
        {points.map((point) => (
          <span className="cpx-site-visits-readout-item" key={`sv-read-${point.date}`}>
            <span className="cpx-site-visits-readout-date">{point.date}</span>
            <span className="cpx-site-visits-readout-value cpx-numeric">
              {point.value.toLocaleString()}
            </span>
          </span>
        ))}
      </div>

      <p className="cpx-chart-suppression-note">
        {days.length} observed {days.length === 1 ? "day" : "days"}
        {siteVisits.timeZone ? ` · dates in the site's ${reportingTimeZoneLabel(siteVisits.timeZone)} calendar` : ""}. Days without a
        reported observation are left out rather than shown as zero.
      </p>
    </section>
  );
}

export default function PublicPlatformReport() {
  const { publicReportToken } = useParams();
  const token = String(publicReportToken || "");

  const [config, setConfig] = useState(null);
  const [phase, setPhase] = useState(REPORT_PHASES.LOADING);
  const [summary, setSummary] = useState(null);
  const [priorSummary, setPriorSummary] = useState(null);
  const [presetKey, setPresetKey] = useState("last_30d");
  const [campaignKey, setCampaignKey] = useState("");
  const [comparison, setComparison] = useState("prior");
  const [metricKey, setMetricKey] = useState("spend");
  const [focusPlatformKey, setFocusPlatformKey] = useState(null);
  const [hoverPlatformKey, setHoverPlatformKey] = useState(null);
  const [describeOpen, setDescribeOpen] = useState(false);
  // Bumped after a successful verification so the configuration effect re-runs
  // against a browser the server now recognizes.
  const [accessEpoch, setAccessEpoch] = useState(0);
  const [accessStatus, setAccessStatus] = useState("");
  const [verifying, setVerifying] = useState(false);

  const reportBase = `${API_BASE_URL}/api/public/reports/${encodeURIComponent(token)}`;

  // ---------------------------------------------------------------------
  // Report configuration: who this report belongs to and what it may show.
  //
  // This is also where the access gate is discovered. A protected report the
  // server has not authorized answers 401 ACCESS_REQUIRED and sends nothing
  // else — no advertiser, no campaigns, no label — so there is nothing here
  // to hide behind an overlay.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!isWellFormedReportToken(token)) {
      setPhase(REPORT_PHASES.UNAVAILABLE);
      return undefined;
    }
    const controller = new AbortController();
    fetch(reportBase, { ...CREDENTIALED, signal: controller.signal })
      .then(async (res) => {
        if (res.status === 401) return { accessRequired: true };
        return res.ok ? res.json() : null;
      })
      .then((payload) => {
        if (controller.signal.aborted) return;
        if (payload?.accessRequired) {
          setConfig(null);
          setPhase(REPORT_PHASES.ACCESS_REQUIRED);
          return;
        }
        if (!payload?.report) {
          setPhase(REPORT_PHASES.UNAVAILABLE);
          return;
        }
        setConfig(payload.report);
        // The default campaign-flight window includes only campaigns the
        // backend lifecycle contract currently admits to ALL CAMPAIGNS. A
        // visible pre-launch campaign therefore cannot extend or dilute the
        // current report merely because its governed row exists.
        const campaigns = payload.report.campaigns || [];
        setCampaignKey("");
        if (buildCampaignFlightPreset(campaigns)) {
          setPresetKey("campaign_flight");
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setPhase(REPORT_PHASES.UNAVAILABLE);
      });
    return () => controller.abort();
  }, [reportBase, token, accessEpoch]);

  /**
   * Submit a security code.
   *
   * The code goes in the request body — never the URL — and the response
   * carries no report content: on success the server sets an HttpOnly cookie
   * and the page simply re-runs its normal, still-governed fetches. Nothing
   * about what the report contains is decided here.
   */
  const submitAccessCode = useCallback(
    async (code) => {
      setVerifying(true);
      setAccessStatus("");
      try {
        const res = await fetch(`${reportBase}/access`, {
          ...CREDENTIALED,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });
        if (res.ok) {
          setAccessStatus("");
          // Leave the gate immediately. The report itself still arrives only
          // if the server serves it — this just avoids showing a code field
          // the recipient has already satisfied.
          setPhase(REPORT_PHASES.LOADING);
          setAccessEpoch((current) => current + 1);
          return;
        }
        if (res.status === 429) {
          const payload = await res.json().catch(() => null);
          const seconds = Number(payload?.retryAfterSeconds || 0);
          setAccessStatus(
            seconds > 0
              ? `Too many attempts. Try again in about ${Math.ceil(seconds / 60)} minute${
                  Math.ceil(seconds / 60) === 1 ? "" : "s"
                }.`
              : "Too many attempts. Please try again shortly."
          );
          return;
        }
        // Every other refusal is one message. The server does not distinguish
        // wrong code from unknown report, and neither does this.
        setAccessStatus("That security code is not valid for this report.");
      } catch {
        setAccessStatus("We could not check that code. Please try again.");
      } finally {
        setVerifying(false);
      }
    },
    [reportBase]
  );

  const campaigns = useMemo(() => config?.campaigns || [], [config]);
  const selectedCampaign = useMemo(
    () =>
      campaigns.find((campaign) => campaign.key === campaignKey) ||
      (campaigns.length === 1 ? campaigns[0] : null),
    [campaigns, campaignKey]
  );
  const flightPreset = useMemo(
    () => buildCampaignFlightPreset(campaigns, campaignKey),
    [campaignKey, campaigns]
  );
  /*
   * The FULL DELIVERY preset is offered only when it ends strictly later
   * than the Campaign Flight window for the same scope — otherwise the two
   * views are identical and the extra chip is clutter.
   */
  const fullDeliveryPreset = useMemo(
    () => buildCampaignFullDeliveryPreset(campaigns, campaignKey),
    [campaignKey, campaigns]
  );
  const fullDeliveryOffered = Boolean(
    flightPreset &&
      fullDeliveryPreset &&
      fullDeliveryPreset.flightEndDate > flightPreset.flightEndDate
  );
  const selectedCampaignNoDelivery = campaignNoDeliveryState(selectedCampaign);
  /*
   * Provenance for a client-authorized Full Delivery extension — stated only
   * for a single-campaign scope, because the two-contract sentence (original
   * approved flight vs authorized delivery window) describes ONE campaign's
   * authorization, never a blend.
   */
  const deliveryExtensionProvenance = useMemo(
    () => campaignDeliveryExtensionProvenance(selectedCampaign),
    [selectedCampaign]
  );
  /*
   * Provenance for the FULL DELIVERY view — same single-campaign rule: the
   * original approved flight, then either the authorized extension or the
   * governed admission that no authorization was recorded.
   */
  const fullDeliveryProvenance = useMemo(
    () => campaignFullDeliveryProvenance(selectedCampaign),
    [selectedCampaign]
  );
  const activePresetKey =
    presetKey === "full_delivery" && !fullDeliveryOffered
      ? flightPreset
        ? "campaign_flight"
        : "last_30d"
      : presetKey === "campaign_flight" && !flightPreset
      ? "last_30d"
      : presetKey;
  const window = useMemo(
    () =>
      resolveReportWindow(
        activePresetKey,
        activePresetKey === "full_delivery" ? fullDeliveryPreset : flightPreset
      ),
    [activePresetKey, flightPreset, fullDeliveryPreset]
  );

  // ---------------------------------------------------------------------
  // Reporting. The only request inputs are the window and, when the report
  // allows a choice, an opaque campaign key issued by this same report.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!config) return undefined;
    const controller = new AbortController();

    const fetchSummary = async (startDate, endDate) => {
      const query = new URLSearchParams({ startDate, endDate });
      if (campaignKey) query.set("campaign", campaignKey);
      const res = await fetch(`${reportBase}/performance-summary?${query.toString()}`, {
        ...CREDENTIALED,
        signal: controller.signal,
      });
      if (res.status === 404) {
        const unavailable = new Error("unavailable");
        unavailable.code = "unavailable";
        throw unavailable;
      }
      // Remembered access can lapse or be revoked mid-session. When it does,
      // the page returns to the gate rather than showing a stale report — the
      // server has already stopped answering, so there is nothing to show.
      if (res.status === 401) {
        const gated = new Error("access_required");
        gated.code = "access_required";
        throw gated;
      }
      if (!res.ok) throw new Error(`summary_failed_${res.status}`);
      return res.json();
    };

    setPhase(REPORT_PHASES.LOADING);
    (async () => {
      try {
        const current = await fetchSummary(window.startDate, window.endDate);
        if (controller.signal.aborted) return;
        let prior = null;
        if (comparison === "prior") {
          try {
            prior = await fetchSummary(window.priorStartDate, window.priorEndDate);
          } catch {
            prior = null;
          }
        }
        if (controller.signal.aborted) return;
        setSummary(current);
        setPriorSummary(prior);
        setPhase(REPORT_PHASES.READY);
      } catch (error) {
        if (controller.signal.aborted) return;
        setSummary(null);
        setPriorSummary(null);
        if (error?.code === "access_required") {
          setConfig(null);
          setPhase(REPORT_PHASES.ACCESS_REQUIRED);
          return;
        }
        setPhase(
          error?.code === "unavailable" ? REPORT_PHASES.UNAVAILABLE : REPORT_PHASES.ERROR
        );
      }
    })();

    return () => controller.abort();
  }, [config, reportBase, campaignKey, comparison, window.startDate, window.endDate, window.priorStartDate, window.priorEndDate]);

  // The shared model — the same function the authenticated page calls.
  const model = useMemo(
    () => buildPlatformAnalyticsModel(summary, priorSummary),
    [summary, priorSummary]
  );

  // The tile "solo" interaction — a presentation choice over data this report
  // already carries, exactly as the authenticated page offers it.
  const view = focusPlatformAnalyticsModel(model, focusPlatformKey) || model;
  const focusedPlatform = view.focusedPlatform || null;
  /*
   * The strip that describes ONE platform, whichever way the page arrived at
   * one: an explicit drill-down, or a campaign whose reporting scope contains
   * a single platform to begin with.
   *
   * Both resolve through the platform's registry profile in the shared model,
   * so they are the same cards in the same order — and on a one-platform
   * campaign they are the same numbers too, because the campaign aggregate is
   * that platform's row. Selecting the sole platform therefore changes the
   * page's scope indicator and nothing a reader is being asked to judge.
   *
   * Null on a mixed flight, on a campaign with no reportable delivery, and on
   * a sole platform that declares no profile of its own — in every one of
   * those the generic campaign strip below renders exactly as before.
   */
  const platformKpiStrip = view.focusedKpiStrip || view.campaignPlatformKpiStrip || null;
  /*
   * Which metrics the delivery chart may be driven by. Resolved AFTER the
   * focus projection because it depends on it: a strip without a Clicks tile
   * must not leave the chart plotting clicks, with nothing on screen to show
   * for the selection or to change it back with. The selection falls back to
   * Spend rather than being silently kept.
   */
  const selectableMetrics = platformKpiStrip
    ? PLATFORM_ANALYTICS_METRICS.filter((entry) =>
        platformKpiStrip.some((tile) => tile.key === entry.key)
      )
    : PLATFORM_ANALYTICS_METRICS;
  const selectedMetric =
    selectableMetrics.find((entry) => entry.key === metricKey) || selectableMetrics[0];
  /* The same eight cards, in the same order, from the same contract as the
     authenticated page — built from the UNFOCUSED model so the qualified-video
     cards keep stating the campaign rather than a soloed platform. */
  const campaignKpiStrip = useMemo(
    () => buildCampaignKpiStrip({ view, model, selectedMetricKey: selectedMetric?.key }),
    [view, model, selectedMetric]
  );
  const togglePlatformFocus = useCallback((platformKey) => {
    setFocusPlatformKey((current) => (current === platformKey ? null : platformKey));
  }, []);

  /*
   * The chart's two governed states — the same builders and the same chart
   * the authenticated page and Quantum render, scoped to what this report is
   * authorized to disclose. When the projected payload carries the daily
   * reporting contract (`dailyByChannel`), delivery is drawn BY DAY, dated
   * stacked columns from dated evidence only; otherwise the window-total
   * platform-axis state stays. Either way each platform's efficiency reads
   * through the one governed resolver: CTV on video view rate, click-bought
   * inventory on CTR.
   */
  const resolveWindowRate = (row) => {
    const resolved = resolvePlatformEfficiencyMetric({
      platformKey: row?.key,
      ...readPlatformCounts(row),
    });
    return resolved
      ? { label: resolved.label, value: formatRatePercent(resolved.value) }
      : null;
  };
  const deliveryChartModel = useMemo(
    () =>
      buildDailyDeliveryChartModel({
        summary,
        model,
        metric: selectedMetric,
      }) ||
      buildWindowTotalsDeliveryChartModel({
        platforms: model.platforms,
        metric: selectedMetric,
        resolveBranding: resolvePlatformBranding,
        formatValue: formatAnalyticsValue,
        window,
        resolveRate: resolveWindowRate,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summary, model, selectedMetric, window.startDate, window.endDate]
  );
  const chartIsDaily = deliveryChartModel.columnAxis === "time";
  const dailyDisclosure = chartIsDaily ? summaryDailyByChannel(summary) : null;

  const resetFilters = useCallback(() => {
    setPresetKey(flightPreset ? "campaign_flight" : "last_30d");
    setMetricKey("spend");
    setComparison("prior");
    setFocusPlatformKey(null);
    setHoverPlatformKey(null);
    if (campaigns.length !== 1) setCampaignKey("");
  }, [campaigns.length, flightPreset]);

  const advertiserName = config?.advertiser?.name || "";
  const logoUrl = config?.advertiser?.logoUrl
    ? `${API_BASE_URL}${config.advertiser.logoUrl}`
    : null;

  /*
   * The gate returns BEFORE any report markup is constructed. There is no
   * analytics content mounted behind it and nothing to reveal with a devtools
   * inspector — the numbers were never sent, because the server refused them.
   */
  if (phase === REPORT_PHASES.ACCESS_REQUIRED) {
    return (
      <ProtectedReportGate
        onSubmit={submitAccessCode}
        status={accessStatus}
        submitting={verifying}
      />
    );
  }

  if (phase === REPORT_PHASES.UNAVAILABLE) {
    return (
      <div className="prx-page" data-public-platform-report="unavailable">
        <div className="prx-unavailable" role="status">
          <p className="prx-unavailable-title">This report is not available</p>
          <p className="prx-unavailable-body">
            The link may have expired, been replaced, or been withdrawn. Please ask your Cphere
            contact for a current link.
          </p>
        </div>
      </div>
    );
  }

  const ready = phase === REPORT_PHASES.READY && model.hasEvidence;

  return (
    <div className="prx-page" data-public-platform-report="true">
      <div className="cpx-page-container prx-page-container">
        <div className="cpx-page-content prx-page-content">
          {/* The canonical Cphere brand mark, in the same position and with
              the same class the authenticated Platform Analytics page uses, so
              the shared `.cphere-brand-header-logo` contract governs its size,
              spacing and responsive behavior on both surfaces. It is rendered
              WITHOUT the authenticated page's onClick: there, the logo doubles
              as a route back to the dashboard, and a public report must not
              offer a way into the application. */}
          <CphereHeaderLogo className="cpx-brand-logo" />

          {/* Same stack the authenticated Platform Analytics page uses: the
              page hero first, the advertiser identity beneath it. Reusing the
              governed stack rather than a bespoke wrapper is what keeps the
              vertical rhythm identical — it owns the hero-to-identity gap and
              neutralises the hero container's global !important margin, so
              neither has to be re-specified (or hacked around) here. */}
          <header className="cphere-governed-hero-selector-stack prx-hero-stack">
            <div className="cpx-hero-container">
              <div
                className="cpx-hero-pill"
                role="heading"
                aria-level="1"
                aria-label="Platform Analytics"
              >
                <span className="cpx-hero-pill-label cphere-hero-pill-wordmark">Platform</span>
                <span className="cpx-hero-pill-tag cphere-hero-pill-tag">Analytics</span>
              </div>
              {/* No subline here — and none on the authenticated pages either
                  any more. The pill states the surface and the advertiser
                  selector immediately below states the scope, so a sentence
                  restating both was carrying nothing. The hero container's
                  flex gap closes the space on its own — nothing compensates
                  for the removal. */}
              <div className="cpx-hero-shimmer-bar" aria-hidden="true" />
            </div>

            {/* The advertiser identity occupies the position the authenticated
                page gives its advertiser selector — but as identity only. This
                report is bound to one advertiser, so there is nothing to
                choose between and no trigger to render. */}
            <div className="prx-advertiser-stage">
              {logoUrl ? (
                <img
                  className="prx-advertiser-logo"
                  src={logoUrl}
                  alt={advertiserName ? `${advertiserName} logo` : ""}
                  loading="eager"
                  decoding="async"
                />
              ) : null}
              {/* The brand mark is usually the advertiser's wordmark, so the
                  name would simply be printed twice. Render it as text only
                  when there is no logo to carry the identity — and the logo's
                  alt text still names the advertiser either way. */}
              {advertiserName && !logoUrl ? (
                <p className="prx-advertiser-name">{advertiserName}</p>
              ) : null}
            </div>
          </header>

          <div className="cpx-analytics-shell prx-analytics-shell">
            <div className="cpx-filter-row prx-filter-row" role="toolbar" aria-label="Report filters">
              {campaigns.length > 1 ? (
                <select
                  className="cpx-select"
                  aria-label="Campaign"
                  value={campaignKey}
                  onChange={(event) => {
                    setCampaignKey(event.target.value);
                    // A different campaign reports a different platform set;
                    // a focus carried across would silently mean something else.
                    setFocusPlatformKey(null);
                    setHoverPlatformKey(null);
                  }}
                >
                  <option value="">ALL CAMPAIGNS</option>
                  {campaigns.map((campaign) => (
                    <option key={campaign.key} value={campaign.key}>
                      {campaignSelectorLabel(campaign)}
                    </option>
                  ))}
                </select>
              ) : null}

              <div className="cpx-chip-group" role="group" aria-label="Date range">
                {flightPreset ? (
                  <button
                    type="button"
                    className={
                      "cpx-chip" +
                      (activePresetKey === flightPreset.key ? " cpx-chip--active" : "")
                    }
                    aria-pressed={activePresetKey === flightPreset.key}
                    onClick={() => setPresetKey(flightPreset.key)}
                  >
                    {flightPreset.label}
                  </button>
                ) : null}
                {fullDeliveryOffered ? (
                  <button
                    type="button"
                    className={
                      "cpx-chip" +
                      (activePresetKey === fullDeliveryPreset.key ? " cpx-chip--active" : "")
                    }
                    aria-pressed={activePresetKey === fullDeliveryPreset.key}
                    onClick={() => setPresetKey(fullDeliveryPreset.key)}
                  >
                    {fullDeliveryPreset.label}
                  </button>
                ) : null}
                {PLATFORM_ANALYTICS_DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    className={
                      "cpx-chip" + (activePresetKey === preset.key ? " cpx-chip--active" : "")
                    }
                    aria-pressed={activePresetKey === preset.key}
                    onClick={() => setPresetKey(preset.key)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <select
                className="cpx-select"
                aria-label="Comparison period"
                value={comparison}
                onChange={(event) => setComparison(event.target.value)}
              >
                <option value="prior">vs prior period</option>
                <option value="none">No comparison</option>
              </select>

              <button type="button" className="cpx-chip" onClick={resetFilters}>
                Reset
              </button>
            </div>

            {/* The FULL DELIVERY view states its own provenance: the original
                approved flight, then either the authorized extension or the
                governed admission that no authorization was recorded. It never
                implies the delivery inside the window was authorized. */}
            {activePresetKey === "full_delivery" && fullDeliveryProvenance ? (
              <p
                className="cpx-flight-provenance"
                role="note"
                data-full-delivery="true"
              >
                ORIGINAL APPROVED FLIGHT ·{" "}
                <span className="cpx-numeric">
                  {fullDeliveryProvenance.flightStartDate} –{" "}
                  {fullDeliveryProvenance.flightEndDate}
                </span>
                {fullDeliveryProvenance.authorizedDeliveryEndDate ? (
                  <>
                    {" "}
                    · AUTHORIZED EXTENSION · THROUGH{" "}
                    <span className="cpx-numeric">
                      {fullDeliveryProvenance.authorizedDeliveryEndDate}
                    </span>
                  </>
                ) : fullDeliveryProvenance.deliveryBeyondAuthorization ? (
                  <> · EXTENSION AUTHORIZATION · NOT RECORDED</>
                ) : null}
              </p>
            ) : /* Two governed date contracts, both visible: the original approved
                flight is history; the authorized delivery window is the current
                execution authority after a Full Delivery extension. */
            deliveryExtensionProvenance ? (
              <p className="cpx-flight-provenance" role="note" data-delivery-extended="true">
                Original approved flight{" "}
                <span className="cpx-numeric">
                  {deliveryExtensionProvenance.flightStartDate} →{" "}
                  {deliveryExtensionProvenance.flightEndDate}
                </span>{" "}
                · Authorized delivery through{" "}
                <span className="cpx-numeric">
                  {deliveryExtensionProvenance.authorizedDeliveryEndDate}
                </span>{" "}
                (Full Delivery)
              </p>
            ) : null}

            {phase === REPORT_PHASES.LOADING ? (
              <div className="cpx-state-panel" role="status" data-analytics-state="loading">
                <p className="cpx-state-title">Loading reporting…</p>
              </div>
            ) : null}

            {phase === REPORT_PHASES.ERROR ? (
              <div className="cpx-state-panel" role="status" data-analytics-state="error">
                <p className="cpx-state-title">Reporting failed to load</p>
                <p className="cpx-state-body">
                  Please try again shortly. No values are shown when reporting cannot be verified.
                </p>
              </div>
            ) : null}

            {phase === REPORT_PHASES.READY && !model.hasEvidence ? (
              <div
                className="cpx-state-panel"
                role="status"
                data-analytics-state={
                  selectedCampaignNoDelivery?.key || "awaiting_platform_data"
                }
              >
                <p className="cpx-state-title">
                  {selectedCampaignNoDelivery?.title || "Awaiting platform data"}
                </p>
                <p className="cpx-state-body">
                  {selectedCampaignNoDelivery?.message ||
                    "No reporting is available for this window yet. Metrics appear here once the connected platforms report delivery — nothing is estimated in the meantime."}
                </p>
                {selectedCampaignNoDelivery &&
                activePresetKey === flightPreset?.key ? (
                  <p className="cpx-state-body">
                    Campaign flight:{" "}
                    <span className="cpx-numeric">
                      {window.startDate} → {window.endDate}
                    </span>
                    .
                  </p>
                ) : model.latestReportingWindow &&
                (model.latestReportingWindow.start !== window.startDate ||
                  model.latestReportingWindow.end !== window.endDate) ? (
                  <p className="cpx-state-body">
                    Most recent reporting window:{" "}
                    <span className="cpx-numeric">
                      {model.latestReportingWindow.start} → {model.latestReportingWindow.end}
                    </span>
                    .
                  </p>
                ) : null}
              </div>
            ) : null}

            {/*
              Site visits render whether or not media has reported.
              They are an INDEPENDENT observation of the site, not a media
              metric — gating them on delivery hid them on exactly the reports
              that need them most: a campaign whose flight has not started, where
              observed traffic is the only signal there is. Found by fetching the
              live public report logged out; no test caught it, because every
              test up to then exercised the branch that already had media.
            */}
            {phase === REPORT_PHASES.READY &&
            !model.hasEvidence &&
            !selectedCampaignNoDelivery ? (
              <PublicSiteVisitsChart siteVisits={summary?.siteVisits} />
            ) : null}

            {ready ? (
              <>
                {focusedPlatform ? (
                  <div className="cpx-focus-banner" role="status">
                    <span
                      className="cpx-swatch"
                      style={{
                        backgroundColor:
                          resolvePlatformBranding(focusedPlatform.key)?.color ||
                          "var(--cphere-cyan, #7ee7ff)",
                      }}
                      aria-hidden="true"
                    />
                    <span className="cpx-focus-banner-label">
                      Focused on {focusedPlatform.label}
                    </span>
                    <button
                      type="button"
                      className="cpx-chip"
                      onClick={() => setFocusPlatformKey(null)}
                    >
                      Show all platforms
                    </button>
                  </div>
                ) : null}

                <div className="cpx-kpi-strip" role="group" aria-label="Summary metrics">
                  {platformKpiStrip ? (
                    /*
                     * The sole platform's OWN primary KPIs, resolved from its
                     * registry profile — byte-for-byte the same cards, order,
                     * labels and formatting the authenticated page and the
                     * dedicated platform page render. Reached by an explicit
                     * drill-down, or by a campaign that only ever had one
                     * platform to aggregate.
                     */
                    platformKpiStrip.map((tile) => {
                      const selectable = selectableMetrics.some((entry) => entry.key === tile.key);
                      const body = (
                        <>
                          <span className="cpx-kpi-label">{tile.label}</span>
                          <span className="cpx-kpi-value cpx-numeric">{tile.display}</span>
                          {tile.note ? <span className="cpx-kpi-delta">{tile.note}</span> : null}
                        </>
                      );
                      return selectable ? (
                        <button
                          key={tile.key}
                          type="button"
                          className={
                            "cpx-kpi-card cpx-kpi-card--selectable" +
                            (tile.key === selectedMetric.key ? " cpx-kpi-card--active" : "")
                          }
                          onClick={() => setMetricKey(tile.key)}
                          aria-pressed={tile.key === selectedMetric.key}
                          title={tile.definition || undefined}
                        >
                          {body}
                        </button>
                      ) : (
                        <div
                          key={tile.key}
                          className="cpx-kpi-card"
                          title={tile.definition || undefined}
                        >
                          {body}
                        </div>
                      );
                    })
                  ) : (
                    /*
                     * The campaign strip, from the single canonical contract.
                     *
                     * This surface is exactly why the contract exists. It
                     * already shared `buildPlatformAnalyticsModel` and still
                     * kept its own copy of the strip markup, so when the
                     * qualified-video migration landed it went on rendering
                     * the old cards for a full deploy cycle. Sharing the model
                     * was never enough; the strip itself had to be shared.
                     */
                    campaignKpiStrip.map((card) => {
                      /* The outcome tile carries the public-report wording of
                         its definition, which is longer than the
                         authenticated one because a client reading this has
                         no analyst beside them. Which wording depends on the
                         governed classification the model resolved: the
                         verified site-visits sentence, or the generic
                         conversions one. */
                      const definition =
                        card.key === "outcome" && card.metricKey === "conversions"
                          ? card.outcomeClassification ===
                            AD_ATTRIBUTED_SITE_VISITS_CLASSIFICATION
                            ? AD_ATTRIBUTED_SITE_VISITS_METRIC_DEFINITION
                            : CONVERSIONS_METRIC_DEFINITION
                          : card.tooltip;
                      const body = (
                        <>
                          <span className="cpx-kpi-label">{card.label}</span>
                          {definition ? (
                            <span style={METRIC_DEFINITION_SR_ONLY_STYLE}>{definition}</span>
                          ) : null}
                          <span className="cpx-kpi-value cpx-numeric">{card.value}</span>
                          {card.delta ? (
                            <span className={`cpx-kpi-delta cpx-kpi-delta--${card.deltaDirection}`}>
                              {card.delta}
                            </span>
                          ) : card.detail ? (
                            <span className="cpx-kpi-delta">{card.detail}</span>
                          ) : null}
                        </>
                      );
                      return card.selectable ? (
                        <button
                          key={card.key}
                          type="button"
                          className={
                            "cpx-kpi-card cpx-kpi-card--selectable" +
                            (card.active ? " cpx-kpi-card--active" : "")
                          }
                          onClick={() => setMetricKey(card.key)}
                          aria-pressed={card.active}
                          title={definition || undefined}
                        >
                          {body}
                        </button>
                      ) : (
                        <div
                          key={card.key}
                          className="cpx-kpi-card"
                          title={definition || undefined}
                        >
                          {body}
                        </div>
                      );
                    })
                  )}
                </div>

                <section className="cpx-panel cpx-panel--hero" aria-label="Delivery and efficiency chart">
                  <div className="cpx-panel-header">
                    <div>
                      <h2 className="cpx-panel-title">Delivery and efficiency</h2>
                      <p className="cpx-panel-subtitle">
                        {window.startDate} → {window.endDate} ·{" "}
                        {chartIsDaily ? "reported by day per platform" : "window totals per platform"} ·{" "}
                        {selectedMetric.label}
                        {focusedPlatform ? ` · ${focusedPlatform.label} focused` : ""}
                      </p>
                      {/* The calendar this report states its days in. */}
                      {reportingTimeZoneStatement(responseReportingTimeZone(summary)) ? (
                        <p className="cpx-panel-subtitle" data-reporting-time-zone="true">
                          {reportingTimeZoneStatement(responseReportingTimeZone(summary))}
                          {sourceCalendarQualification(summary) ? ` · ${sourceCalendarQualification(summary)}` : ""}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <DeliveryChart
                    model={deliveryChartModel}
                    mode="absolute"
                    highlightPlatform={hoverPlatformKey || focusPlatformKey}
                    focusPlatform={focusPlatformKey}
                    onTogglePlatform={togglePlatformFocus}
                  />
                  {/* What the picture above actually is. The dated state says
                      how many days reported and, when some delivery could only
                      be reported as a window total, that those dollars are not
                      in the columns. The window-total state keeps naming what
                      is absent and why. */}
                  <p className="cpx-chart-suppression-note">
                    {chartIsDaily
                      ? `Values are reported by day across ${deliveryChartModel.columns.length} reporting day${
                          deliveryChartModel.columns.length === 1 ? "" : "s"
                        }; days without reported delivery are left out rather than shown as zero.${
                          dailyDisclosure?.complete === false
                            ? " Delivery reported only as a window total is absent from these columns."
                            : ""
                        }`
                      : "Values are reported window totals per platform. The daily series, the efficiency trend, the plan step and the projection appear in this chart once the daily reporting contract is certified — nothing is interpolated in the meantime."}
                  </p>
                  {/* The chart's footer: what the marks mean, and the way to
                      read them in words. Grouped so the stylesheet can centre
                      them under the chart as one thing. */}
                  <div className="cpx-chart-footer">
                    <DeliveryChartLegend model={deliveryChartModel} mode="absolute" />
                    <button
                      type="button"
                      className="cpx-describe-toggle"
                      aria-expanded={describeOpen}
                      onClick={() => setDescribeOpen((open) => !open)}
                    >
                      Describe this chart in words
                    </button>
                    {describeOpen ? (
                      <p className="cpx-describe-text">{deliveryChartModel.textSummary}</p>
                    ) : null}
                  </div>
                </section>

                {/*
                  Site visits are ONE visualization, not two. When the daily reporting
                  contract is certified the governed series rides the Delivery
                  chart's own date axis on its right-hand scale, which is where
                  it belongs: same dates, one picture, no scrolling between two
                  charts to ask whether traffic moved with delivery.

                  This standalone panel survives for the state that cannot host
                  the overlay — a scope with no certified daily series, whose
                  Delivery chart is a platform axis with no dates to attach a
                  traffic point to. Dropping it there would delete the governed
                  site-visit evidence from the report rather than consolidate it.
                */}
                {deliveryChartModel?.siteVisits?.available ? null : (
                  <PublicSiteVisitsChart siteVisits={summary?.siteVisits} />
                )}

                {/* The canonical Platform Analytics contribution rail. Same
                    component, same hierarchy and same governed absences as
                    Quantum's — a shared report is not a lesser presentation of
                    the same platform. */}
                <div className="cpx-platform-rail" role="group" aria-label="Platform contribution — select a platform to focus its stats">
                  {model.platforms.map((row) => {
                    const soloed = focusPlatformKey === row.key;
                    return (
                      <PlatformContributionCard
                        key={"rail-" + row.key}
                        {...buildPlatformCardPresentation(row)}
                        soloed={soloed}
                        inactive={Boolean(focusPlatformKey) && !soloed}
                        onClick={() => togglePlatformFocus(row.key)}
                        onMouseEnter={() => setHoverPlatformKey(row.key)}
                        onMouseLeave={() => setHoverPlatformKey(null)}
                      >
                        {Array.isArray(row.subChannels) && row.subChannels.length ? (
                          <ul
                            className="cpx-platform-subchannel-list"
                            aria-label={`${row.label} channel breakdown`}
                          >
                            {row.subChannels.map((sub) => (
                              <li className="cpx-platform-subchannel" key={"sub-" + sub.key}>
                                <span className="cpx-platform-subchannel-name">{sub.label}</span>
                                <span className="cpx-platform-subchannel-value cpx-numeric">
                                  {sub.spend > 0
                                    ? `$${sub.spend.toLocaleString("en-US", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`
                                    : `${formatAnalyticsValue(sub.impressions)} impr.`}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </PlatformContributionCard>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>

          {/* ONE Cphere footer, and it is the approved one: the same shared
              brand footer component the Campaign Strategy Microsites render —
              same brain mark, same typography, same divider, same spacing,
              same responsive wrap. This page used to carry its own credit
              line and its own footer rules; it no longer has either.

              What is report-specific is passed IN rather than rebuilt around
              it: the credit names the report's own campaign (still fully
              dynamic — no advertiser or title is hard-coded anywhere on this
              surface), and the scope disclosure a shared link owes its viewer
              rides inside that same footer element instead of stacking a
              second branded block beneath it. */}
          <CphereBrandFooter
            className="prx-footer"
            lead={PUBLIC_REPORT_FOOTER_LEAD}
            trail={(selectedCampaign && selectedCampaign.name) || ""}
            textClassName="prx-footer-credit"
            note={
              <p className="prx-footer-note">
                Shared report — view only. Figures reflect canonical platform reporting for the
                selected window.
              </p>
            }
          />
        </div>
      </div>
    </div>
  );
}
